import { useMemo, useState } from 'react';

import { ChevronsUpDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ExportButton } from '@/components/atoms/ExportButton/ExportButton';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

import { useComparisonMode } from '../../hooks/useComparisonMode';
import { useHumanAgentsExport } from '../../hooks/useHumanAgentsExport';
import {
  useHumanAgentsPerformance,
  type HumanAgentPerformanceMetrics,
  type HumanAgentPerformanceRow,
} from '../../hooks/useHumanAgentsPerformance';
import { selectAnalyticsDates } from '../../redux/selectors';
import { formatDateRangeLabel } from '../../utils/formatDateRangeLabel';
import { formatDurationFromSeconds } from '../../utils/formatDuration';
import { AnalyticsEmptyState } from '../aiAgents/AnalyticsEmptyState';
import { CompareTrendCell } from '../aiAgents/CompareTrendCell';

type SortDirection = 'asc' | 'desc';
type SortKey = 'name' | 'sessions' | 'handling' | 'response' | 'resolution' | CsatSortKey;
type CsatSortKey = 'userCsat' | 'aiCsat';

interface ColumnDef {
  key: SortKey;
  labelKey: string;
  align: 'left' | 'right';
  defaultDirection: SortDirection;
  widthClass?: string;
}

const NUMERIC_COLUMN_WIDTH = 'w-32';

const COLUMNS: ColumnDef[] = [
  {
    key: 'name',
    labelKey: 'analytics.human_agents_performance.column_name',
    align: 'left',
    defaultDirection: 'asc',
  },
  {
    key: 'sessions',
    labelKey: 'analytics.human_agents_performance.column_sessions',
    align: 'right',
    defaultDirection: 'desc',
    widthClass: NUMERIC_COLUMN_WIDTH,
  },
  {
    key: 'handling',
    labelKey: 'analytics.human_agents_performance.column_handling_time',
    align: 'right',
    defaultDirection: 'asc',
    widthClass: NUMERIC_COLUMN_WIDTH,
  },
  {
    key: 'response',
    labelKey: 'analytics.human_agents_performance.column_response_time',
    align: 'right',
    defaultDirection: 'asc',
    widthClass: NUMERIC_COLUMN_WIDTH,
  },
  {
    key: 'resolution',
    labelKey: 'analytics.human_agents_performance.column_resolution_time',
    align: 'right',
    defaultDirection: 'asc',
    widthClass: NUMERIC_COLUMN_WIDTH,
  },
  {
    key: 'userCsat',
    labelKey: 'analytics.human_agents_performance.column_user_csat',
    align: 'right',
    defaultDirection: 'desc',
    widthClass: NUMERIC_COLUMN_WIDTH,
  },
  {
    key: 'aiCsat',
    labelKey: 'analytics.human_agents_performance.column_ai_csat',
    align: 'right',
    defaultDirection: 'desc',
    widthClass: NUMERIC_COLUMN_WIDTH,
  },
];

const numberFormatter = new Intl.NumberFormat('en-US');

const EMPTY_VALUE_PLACEHOLDER = '-';

const SKELETON_ROW_KEYS = ['r1', 'r2', 'r3', 'r4', 'r5'];

const isCsatEmpty = (value: number) => value <= 0;

const compareRows = (a: HumanAgentPerformanceRow, b: HumanAgentPerformanceRow, key: SortKey) => {
  switch (key) {
    case 'name':
      return a.name.localeCompare(b.name);
    case 'sessions':
      return a.sessions - b.sessions;
    case 'handling':
      return a.handlingSeconds - b.handlingSeconds;
    case 'response':
      return a.responseSeconds - b.responseSeconds;
    case 'resolution':
      return a.resolutionSeconds - b.resolutionSeconds;
    case 'userCsat':
      return a.userCsat - b.userCsat;
    case 'aiCsat':
      return a.aiCsat - b.aiCsat;
  }
};

const formatCsat = (value: number) => value.toFixed(1);

interface NumericCellProps {
  current: number;
  previous: number | null;
  formatValue: (value: number) => string;
  showCompare: boolean;
  /** True when a *decrease* is favorable (e.g. handling/response/resolution time). */
  invertTrend?: boolean;
  /** When true, render a placeholder and suppress the trend chip. */
  isEmpty?: boolean;
  /** When true, render the value but suppress the trend chip (previous period has no samples). */
  isPreviousEmpty?: boolean;
}

const NumericCell = ({
  current,
  previous,
  formatValue,
  showCompare,
  invertTrend,
  isEmpty,
  isPreviousEmpty,
}: NumericCellProps) => {
  if (isEmpty) {
    return <>{EMPTY_VALUE_PLACEHOLDER}</>;
  }
  if (!showCompare || previous == null || isPreviousEmpty) {
    return <>{formatValue(current)}</>;
  }
  return (
    <div className="inline-flex w-full items-center justify-end gap-3">
      <CompareTrendCell
        current={current}
        previous={previous}
        formatValue={formatValue}
        invertColors={invertTrend}
      />
      <span>{formatValue(current)}</span>
    </div>
  );
};

const METRIC_KEY: Record<Exclude<SortKey, 'name'>, keyof HumanAgentPerformanceMetrics> = {
  sessions: 'sessions',
  handling: 'handlingSeconds',
  response: 'responseSeconds',
  resolution: 'resolutionSeconds',
  userCsat: 'userCsat',
  aiCsat: 'aiCsat',
};

const CSAT_SORT_KEYS: CsatSortKey[] = ['userCsat', 'aiCsat'];

const isCsatSortKey = (key: SortKey): key is CsatSortKey => key === 'userCsat' || key === 'aiCsat';

const previousValueFor = (
  row: HumanAgentPerformanceRow,
  key: Exclude<SortKey, 'name'>
): number | null => (row.previous ? row.previous[METRIC_KEY[key]] : null);

export const HumanAgentsPerformance = () => {
  const { t } = useTranslation();
  const { isComparing } = useComparisonMode();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);

  const dateRange = useMemo(() => formatDateRangeLabel(startDate, endDate), [startDate, endDate]);

  const [sortKey, setSortKey] = useState<SortKey>('sessions');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const { data: performanceRows, isLoading } = useHumanAgentsPerformance();
  const { exportWidget, isWidgetExportable, isExporting } = useHumanAgentsExport();

  const rows = useMemo(() => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...performanceRows].sort((a, b) => {
      if (isCsatSortKey(sortKey)) {
        const aEmpty = isCsatEmpty(a[sortKey]);
        const bEmpty = isCsatEmpty(b[sortKey]);
        if (aEmpty !== bEmpty) {
          return aEmpty ? 1 : -1;
        }
      }
      return dir * compareRows(a, b, sortKey);
    });
  }, [performanceRows, sortKey, sortDirection]);

  const handleSort = (col: ColumnDef) => {
    if (col.key === sortKey) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(col.key);
    setSortDirection(col.defaultDirection);
  };

  const ariaSortFor = (key: SortKey): 'ascending' | 'descending' | 'none' => {
    if (key !== sortKey) {
      return 'none';
    }
    return sortDirection === 'asc' ? 'ascending' : 'descending';
  };

  return (
    <Card className="rounded-lg bg-background text-foreground shadow-none">
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-2">
            <CardTitle>{t('analytics.human_agents_performance.title')}</CardTitle>
            <CardDescription>{dateRange}</CardDescription>
          </div>
          {isWidgetExportable('human_agents_performance') && (
            <ExportButton
              variant="icon"
              onExport={(format) => exportWidget('human_agents_performance', format)}
              isExporting={isExporting}
            />
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="space-y-2">
            {SKELETON_ROW_KEYS.map((key) => (
              <Skeleton key={key} className="h-10 w-full rounded-lg" />
            ))}
          </div>
        )}
        {!isLoading && rows.length === 0 && (
          <div className="flex items-center justify-center py-6">
            <AnalyticsEmptyState
              title={t('analytics.no_data_available')}
              description={t('analytics.no_data_for_period')}
            />
          </div>
        )}
        {!isLoading && rows.length > 0 && (
          <Table className="max-h-96">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {COLUMNS.map((col) => (
                  <TableHead
                    key={col.key}
                    aria-sort={ariaSortFor(col.key)}
                    className={cn('px-4', col.widthClass)}
                  >
                    <button
                      type="button"
                      onClick={() => handleSort(col)}
                      className={cn(
                        'inline-flex items-center gap-1 text-sm leading-6 font-medium text-foreground-muted hover:text-foreground',
                        col.align === 'right' && 'w-full justify-end'
                      )}
                    >
                      {t(col.labelKey)}
                      <ChevronsUpDownIcon className="size-3.5 text-foreground-muted" />
                    </button>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="max-w-0 px-4">
                    <InfoTooltip content={row.name} size="auto">
                      <span className="block truncate text-sm leading-5 font-normal text-foreground">
                        {row.name}
                      </span>
                    </InfoTooltip>
                  </TableCell>
                  <TableCell
                    className={cn(
                      NUMERIC_COLUMN_WIDTH,
                      'px-4 text-right text-sm leading-5 font-normal text-foreground tabular-nums'
                    )}
                  >
                    <NumericCell
                      current={row.sessions}
                      previous={previousValueFor(row, 'sessions')}
                      formatValue={(v) => numberFormatter.format(v)}
                      showCompare={isComparing}
                    />
                  </TableCell>
                  <TableCell
                    className={cn(
                      NUMERIC_COLUMN_WIDTH,
                      'px-4 text-right text-sm leading-5 font-normal text-foreground tabular-nums'
                    )}
                  >
                    <NumericCell
                      current={row.handlingSeconds}
                      previous={previousValueFor(row, 'handling')}
                      formatValue={(v) => formatDurationFromSeconds(v)}
                      showCompare={isComparing}
                      invertTrend
                    />
                  </TableCell>
                  <TableCell
                    className={cn(
                      NUMERIC_COLUMN_WIDTH,
                      'px-4 text-right text-sm leading-5 font-normal text-foreground tabular-nums'
                    )}
                  >
                    <NumericCell
                      current={row.responseSeconds}
                      previous={previousValueFor(row, 'response')}
                      formatValue={(v) => formatDurationFromSeconds(v)}
                      showCompare={isComparing}
                      invertTrend
                    />
                  </TableCell>
                  <TableCell
                    className={cn(
                      NUMERIC_COLUMN_WIDTH,
                      'px-4 text-right text-sm leading-5 font-normal text-foreground tabular-nums'
                    )}
                  >
                    <NumericCell
                      current={row.resolutionSeconds}
                      previous={previousValueFor(row, 'resolution')}
                      formatValue={(v) => formatDurationFromSeconds(v)}
                      showCompare={isComparing}
                      invertTrend
                    />
                  </TableCell>
                  {CSAT_SORT_KEYS.map((key) => (
                    <TableCell
                      key={key}
                      className={cn(
                        NUMERIC_COLUMN_WIDTH,
                        'px-4 text-right text-sm leading-5 font-normal text-foreground tabular-nums'
                      )}
                    >
                      <NumericCell
                        current={row[key]}
                        previous={previousValueFor(row, key)}
                        formatValue={formatCsat}
                        showCompare={isComparing}
                        isEmpty={isCsatEmpty(row[key])}
                        isPreviousEmpty={row.previous ? isCsatEmpty(row.previous[key]) : true}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
